'use client';

import { FC, SVGProps, useMemo } from 'react';
import { ReactComponent as Circles } from '@assets/icons/circles.svg';
import { ReactComponent as CommentSmile } from '@assets/icons/comment-smile.svg';
import { ReactComponent as GenderIcon } from '@assets/icons/person-half-dress.svg';
import config from 'config';
import { useFormik } from 'formik';

import {
  Box,
  Center,
  Flex,
  useBreakpointValue,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { GenderFormType, ReactSelectItemInterface } from 'types';

import { IconTextField, SelectField } from 'ui';
import { AuthStepButtons } from '../../../AuthStepButtons';

interface PersonalizationProps {
  formState: GenderFormType;
  onSubmit: (values: GenderFormType) => void;
  onBack: () => void;
}

type FieldsType = {
  fieldName: keyof GenderFormType;
  placeholder: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
  options: ReactSelectItemInterface[];
};

const {
  login: { isPronounceAvailable, genderValues, genderPlaceholder }
} = config;

const Personalization = ({
  onSubmit,
  formState,
  onBack
}: PersonalizationProps) => {
  const { colors, fonts } = useTheme();
  const {
    palette: {
      auth: {
        createAccount: { accountForm: palette }
      }
    }
  } = colors;
  const fontSize = useBreakpointValue({ base: 16, sm: 18, md: 22 });
  const { userPronouns } = useAppSelector((state) => state.user);

  const {
    values,
    errors,
    setFieldValue,
    setFieldTouched,
    touched,
    validateField,
    handleSubmit,
    getFieldProps
  } = useFormik({
    initialValues: formState,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit
  });

  const handleInputChange = (name: string) => (value: string) => {
    setFieldValue(name, value);
  };

  const buttonLabel = Object.values(values).some((val) => Boolean(val))
    ? 'Next'
    : 'Skip';

  const getError = (fieldName: keyof GenderFormType): string | undefined =>
    errors[fieldName];

  const pronounceOptions: ReactSelectItemInterface[] = useMemo(
    () =>
      (userPronouns ?? []).map(({ name, id }) => ({
        label: name,
        value: String(id)
      })),
    [userPronouns]
  );

  const selectFields: FieldsType[] = useMemo(() => {
    const fieldsList = [
      {
        fieldName: 'gender' as keyof GenderFormType,
        placeholder: genderPlaceholder,
        Icon: GenderIcon,
        options: genderValues
      }
    ];

    if (isPronounceAvailable && Boolean(pronounceOptions.length)) {
      fieldsList.unshift({
        fieldName: 'pronoun_id',
        placeholder: 'PRONOUNS',
        Icon: Circles,
        options: pronounceOptions
      });
    }

    return fieldsList;
  }, [pronounceOptions]);

  return (
    <Box
      as='form'
      onSubmit={handleSubmit}
      width='100%'
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
    >
      <VStack gap='10px' alignItems='center' w='full'>
        <IconTextField
          autoFocus
          w='full'
          helperText={
            <>
              Nickname/preferred name,
              <br />
              such as Sam for Samantha
            </>
          }
          pb={{ base: 0.5, md: 1 }}
          {...getFieldProps('nickname')}
          placeholder='Preferred Name'
          iconBg={palette.iconBg}
          onChange={handleInputChange('nickname')}
          error={getError('nickname')}
          leftIcon={
            <CommentSmile
              width={24}
              height={24}
              fill={palette.iconColor}
              opacity={0.6}
            />
          }
        />
        {selectFields.map(({ fieldName, placeholder, Icon, options }) => (
          <Flex alignItems='center' w='full' key={fieldName}>
            <Center
              borderLeftRadius='6px'
              height='60px'
              w='60px'
              minW='60px'
              bg={palette.iconBg}
            >
              <Icon
                width={30}
                height={30}
                fill={palette.iconColor}
                opacity={0.6}
              />
            </Center>
            <SelectField
              name={fieldName}
              setFieldValue={setFieldValue}
              validateField={validateField}
              setFieldTouched={setFieldTouched}
              value={values[fieldName]!}
              options={options}
              error={getError(fieldName)}
              touched={!!touched[fieldName]}
              placeholder={placeholder}
              dropdownIndicatorStyles={{
                marginRight: '11px',
                marginTop: '2px'
              }}
              stylesMenu={{
                background: palette.dropdownBg
              }}
              stylesControl={{
                fontFamily: fonts.main,
                fontSize: fontSize || 22,
                fontWeight: 600,
                background: palette.dropdownBg,
                color: palette.dropdownText,
                height: 60,
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
                marginBottom: 0,
                padding: '0 10px'
              }}
              stylesMenuList={{
                maxHeight: 'auto'
              }}
              stylesPlaceholder={{
                fontSize: '15px',
                fontFamily: fonts.main,
                fontWeight: 500,
                textTransform: 'uppercase',
                color: palette.placeholder,
                letterSpacing: 0.5,
                marginTop: '3px'
              }}
            />
          </Flex>
        ))}
      </VStack>

      <AuthStepButtons
        mt={{ base: '40px', md: '50px' }}
        leftLabel='Back'
        rightLabel={buttonLabel}
        leftHandler={onBack}
      />
    </Box>
  );
};

export default Personalization;
